import { TYPES_DISCOUNT, TYPE_BILL_ITEM } from '../constants/types';
import { Bill, BillItem, DocumentStatus, DocumentType } from '../pages/hospital/Billing/types';

export interface QuickPayEligibility {
    eligible: boolean;
    reason?: string;
    patientAmountDue?: number;
    /** True when Quick Pay will close a DRAFT bill before recording payment. */
    willCloseBill?: boolean;
}

export function isQuickPayableBillStatus(status: DocumentStatus): boolean {
    return status === DocumentStatus.DRAFT || status === DocumentStatus.CLOSED;
}

export function getQuickPayChargeableLines(bill: Bill): BillItem[] {
    return (bill.billItems ?? []).filter(isChargeableLine);
}

export interface QuickPayEligibilityContext {
    externalCoverageAmount?: number;
    cashPaymentMethodId?: number | null;
    /** When false, eligibility fails closed (claims API error). */
    claimsLoaded?: boolean;
}

function isChargeableLine(item: BillItem): boolean {
    if (TYPES_DISCOUNT.includes(item.type)) return false;
    if (item.type === TYPE_BILL_ITEM.HIDDEN_VALUE) return false;
    if (item.type === TYPE_BILL_ITEM.AUTO_ANAESTHESIA) return false;
    if (item.type === TYPE_BILL_ITEM.PERCENT_CHARGE) return false;
    return true;
}

/**
 * SHER-453: conservative eligibility for one-click consultation Quick Pay (v1).
 */
export function evaluateQuickPayEligibility(
    bill: Bill | null | undefined,
    context: QuickPayEligibilityContext = {},
): QuickPayEligibility {
    const externalCoverageAmount = context.externalCoverageAmount ?? 0;

    if (context.claimsLoaded === false) {
        return { eligible: false, reason: 'claims_load_failed' };
    }
    if (!bill) {
        return { eligible: false, reason: 'no_bill' };
    }
    if (!isQuickPayableBillStatus(bill.status)) {
        return { eligible: false, reason: 'invalid_status' };
    }
    if (bill.type !== DocumentType.INVOICE) {
        return { eligible: false, reason: 'not_invoice' };
    }
    if ((bill.totalPaid ?? 0) > 0) {
        return { eligible: false, reason: 'already_paid' };
    }

    const chargeableLines = getQuickPayChargeableLines(bill);
    if (chargeableLines.length !== 1) {
        return { eligible: false, reason: 'not_single_line' };
    }

    const line = chargeableLines[0];
    if (line.type === TYPE_BILL_ITEM.PACKAGE) {
        return { eligible: false, reason: 'package' };
    }
    if ((line.quantity ?? 1) !== 1) {
        return { eligible: false, reason: 'quantity_not_one' };
    }

    const patientAmountDue = Math.max(0, (bill.totalGross ?? 0) - externalCoverageAmount - (bill.totalDiscount ?? 0));
    if (patientAmountDue < 0) {
        return { eligible: false, reason: 'negative_balance' };
    }

    // v1: skip partial insurance / PEC — any external coverage must cover the full gross.
    if (externalCoverageAmount > 0 && externalCoverageAmount < (bill.totalGross ?? 0) - (bill.totalDiscount ?? 0)) {
        return { eligible: false, reason: 'partial_coverage' };
    }

    if (patientAmountDue > 0 && !context.cashPaymentMethodId) {
        return { eligible: false, reason: 'no_cash_method' };
    }

    return {
        eligible: true,
        patientAmountDue,
        willCloseBill: bill.status === DocumentStatus.DRAFT,
    };
}

/**
 * SHER-453 (appointment-scope): pre-check for Quick Pay on ONE appointment line of a shared
 * invoice. Mirrors sherwood-api/src/utils/quickPayEligibility.ts `evaluateQuickPayLineEligibility`.
 * The API is authoritative; this only drives the button + confirmation amount.
 */
export function evaluateQuickPayLineEligibility(
    bill: Bill | null | undefined,
    targetRequestServiceInvestigationId: number,
    context: QuickPayEligibilityContext = {},
): QuickPayEligibility {
    const externalCoverageAmount = context.externalCoverageAmount ?? 0;

    if (context.claimsLoaded === false) {
        return { eligible: false, reason: 'claims_load_failed' };
    }
    if (!bill) {
        return { eligible: false, reason: 'no_bill' };
    }
    if (bill.status !== DocumentStatus.DRAFT) {
        return { eligible: false, reason: 'invalid_status' };
    }
    if (bill.type !== DocumentType.INVOICE) {
        return { eligible: false, reason: 'not_invoice' };
    }

    const hasSpecialLine = (bill.billItems ?? []).some((item) => !isChargeableLine(item));
    if (hasSpecialLine) {
        return { eligible: false, reason: 'has_special_lines' };
    }
    if (externalCoverageAmount > 0) {
        return { eligible: false, reason: 'partial_coverage' };
    }

    const line = (bill.billItems ?? []).find(
        (item) => item.requestServiceInvestigationId === targetRequestServiceInvestigationId,
    );
    if (!line) {
        return { eligible: false, reason: 'line_not_on_bill' };
    }
    if (line.paid) {
        return { eligible: false, reason: 'already_paid' };
    }
    if (line.type === TYPE_BILL_ITEM.PACKAGE) {
        return { eligible: false, reason: 'package' };
    }
    if ((line.quantity ?? 1) !== 1) {
        return { eligible: false, reason: 'quantity_not_one' };
    }

    const patientAmountDue = Math.max(0, (line.amount ?? 0) * (line.quantity ?? 1));
    if (patientAmountDue > 0 && !context.cashPaymentMethodId) {
        return { eligible: false, reason: 'no_cash_method' };
    }

    return { eligible: true, patientAmountDue, willCloseBill: false };
}
